import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch, type Control } from 'react-hook-form';
import type { InventoryItem } from '../../types/inventory';
import { profileService, type CuppingProfile } from '../../services/profileService';
import { clsx } from 'clsx';
import { useLanguage } from '../../features/language/useLanguage';

interface FinishedGoodsTasteProfileFormProps {
  item: InventoryItem;
  onSuccess: () => void;
  onCancel: () => void;
  onViewOtherProfiles?: () => void;
  existingProfileId?: string;
  mode?: 'add' | 'view';
  readOnly?: boolean;
}

interface FinishedGoodsTasteProfileFormValues {
  acidity: number;
  body: number;
  sweetness: number;
  flavorNotes: string[];
  notes: string;
}

interface FlavorGroup {
  name: string;
  children?: string[];
}

const FLAVOR_HIERARCHY: FlavorGroup[] = [
  { name: 'Floral' },
  { name: 'Fruity', children: ['Berry', 'Dried Fruit', 'Citrus Fruit'] },
  { name: 'Sour/Fermented', children: ['Sour', 'Fermented'] },
  { name: 'Green/Vegetative' },
  { name: 'Other', children: ['Chemical', 'Musty/Earthy', 'Woody'] },
  { name: 'Roasted', children: ['Cereal', 'Burnt', 'Tobacco'] },
  { name: 'Nutty/Cocoa', children: ['Nutty', 'Cocoa'] },
  { name: 'Spice' },
  { name: 'Sweet', children: ['Vanilla/Vanillin', 'Brown Sugar'] },
];

const SCALE_MIN = 1;
const SCALE_MAX = 5;

interface TasteSliderRowProps {
  control: Control<FinishedGoodsTasteProfileFormValues>;
  name: keyof FinishedGoodsTasteProfileFormValues;
  label: string;
  leftLabel: string;
  centerLabel: string;
  rightLabel: string;
  disabled?: boolean;
}

const TasteSliderRow: React.FC<TasteSliderRowProps> = ({
  control,
  name,
  label,
  leftLabel,
  centerLabel,
  rightLabel,
  disabled,
}) => {
  const { t } = useLanguage();
  const value = useWatch({ control, name }) ?? SCALE_MIN;

  return (
    <div className="flex flex-col space-y-1">
      <div className="flex justify-between items-end">
        <label htmlFor={name} className="text-sm font-medium text-gray-800 leading-none">
          {label}
        </label>
        <span className="text-xs font-mono font-bold text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded border border-primary-100 min-w-[1.5rem] text-center">
          {value}
        </span>
      </div>
      <div className="relative pt-1">
        <input
          id={name}
          type="range"
          min={SCALE_MIN}
          max={SCALE_MAX}
          step={1}
          {...control.register(name, { valueAsNumber: true })}
          className={clsx(
            "w-full h-2 bg-gray-200 rounded-lg appearance-none accent-gray-800",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          )}
          disabled={disabled}
        />
        <div
          className="absolute top-1 left-0 right-0 flex justify-between px-1 pointer-events-none opacity-30"
          aria-hidden="true"
        >
          {Array.from({ length: SCALE_MAX - SCALE_MIN + 1 }).map((_, index) => (
            <div key={index} className="w-px h-2 bg-gray-800" />
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 font-medium mt-0.5 uppercase tracking-tighter">
          <span>{leftLabel === 'Low' ? t('cupping.formFields.low') : leftLabel === 'Light' ? t('cupping.formFields.light') : leftLabel}</span>
          <span>{centerLabel === 'Medium' ? t('cupping.formFields.medium') : centerLabel}</span>
          <span>{rightLabel === 'High' ? t('cupping.formFields.high') : rightLabel === 'Full' ? t('inventory.finishedGoodsTaste.full') : rightLabel}</span>
        </div>
      </div>
    </div>
  );
};

export const FinishedGoodsTasteProfileForm: React.FC<FinishedGoodsTasteProfileFormProps> = ({
  item,
  onSuccess,
  onCancel,
  onViewOtherProfiles,
  existingProfileId,
  mode = 'view',
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<CuppingProfile[]>([]);

  const emptyValues = useMemo(
    () => ({
      acidity: 1,
      body: 1,
      sweetness: 1,
      flavorNotes: [],
      notes: '',
    }),
    []
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<FinishedGoodsTasteProfileFormValues>({
    defaultValues: emptyValues,
  });

  const flavorNotesValue = useWatch({ control, name: 'flavorNotes' });
  const flavorNotes = useMemo(() => flavorNotesValue || [], [flavorNotesValue]);

  const applyProfileToForm = useCallback(
    (profile: CuppingProfile) => {
      reset({
        acidity: profile.acidity ? Math.round(profile.acidity) : 3,
        body: profile.body ? Math.round(profile.body) : 3,
        sweetness: profile.sweetness ? Math.round(profile.sweetness) : 3,
        flavorNotes: profile.flavor_notes || [],
        notes: profile.notes || '',
      });
    },
    [reset]
  );

  const getProfileTimestamp = useCallback((profile: CuppingProfile) => {
    const dateValue = profile.created_at || profile.cupping_date;
    const time = dateValue ? new Date(dateValue).getTime() : 0;
    return Number.isNaN(time) ? 0 : time;
  }, []);

  useEffect(() => {
    const loadExistingProfile = async () => {
      if (mode === 'add') {
        return;
      }
      if (!existingProfileId && !item.id) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const profiles = await profileService.getCuppingProfileByEntity('ItemType', item.id);
        const matching = profiles.filter((candidate) => {
          const extended = candidate.extended_data as Record<string, unknown> | undefined;
          return (
            candidate.cupping_type === 'FinishedGoodsTaste' ||
            extended?.profile_variant === 'finished_goods_simple'
          );
        });
        const sorted = [...matching].sort((a, b) => getProfileTimestamp(b) - getProfileTimestamp(a));
        const profile =
          (existingProfileId ? sorted.find(candidate => candidate.id === existingProfileId) : undefined) ||
          sorted[0];
        setProfiles(sorted);

        if (profile) {
          applyProfileToForm(profile);
        }
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status && status !== 404) {
          setError(t('inventory.finishedGoodsTaste.errors.loadExistingFailed'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadExistingProfile();
  }, [item.id, existingProfileId, reset, mode, applyProfileToForm, getProfileTimestamp, t]);

  useEffect(() => {
    if (mode === 'add') {
      setError(null);
      setLoading(false);
      reset(emptyValues);
      setProfiles([]);
    }
  }, [mode, reset, emptyValues]);

  const allFlavorValues = useMemo(() => flavorNotes, [flavorNotes]);

  const handleFlavorToggle = (value: string) => {
    const current = new Set(allFlavorValues);
    if (current.has(value)) {
      current.delete(value);
    } else {
      current.add(value);
    }
    setValue('flavorNotes', Array.from(current), { shouldValidate: true });
  };

  const onSubmit = async (values: FinishedGoodsTasteProfileFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        cupping_type: 'FinishedGoodsTaste',
        cupping_date: new Date().toISOString().slice(0, 10),
        score: 0,
        notes: values.notes,
        descriptors: '',
        fragrance_aroma: 0,
        flavor: 0,
        aftertaste: 0,
        acidity: values.acidity,
        sweetness: values.sweetness,
        mouthfeel: 0,
        balance: 0,
        overall: 0,
        roast_level: 0,
        is_qc_passed: undefined,
        fragrance: 0,
        aroma: 0,
        body: values.body,
        extended_data: {
          profile_variant: 'finished_goods_simple',
        },
        defects: [],
        flavor_notes: values.flavorNotes,
        mouthfeel_notes: [],
        type: 'ItemType',
        inventory_item_id: item.id,
        lot_id: undefined,
        sample_id: undefined,
      };

      await profileService.createCuppingProfile(payload);

      onSuccess();
    } catch (err: unknown) {
      const maybeResponse = (err as { response?: { data?: { error?: unknown; message?: unknown } } })?.response;
      const maybeData = maybeResponse?.data;
      const errorMessage =
        maybeData && typeof maybeData.error === 'string' ? maybeData.error : undefined;
      const responseMessage =
        maybeData && typeof maybeData.message === 'string' ? maybeData.message : undefined;
      setError(errorMessage || responseMessage || t('inventory.finishedGoodsTaste.errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        data-test-id="finished-goods-taste-profile-form"
      >
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-test-id="finished-goods-taste-error">
            {error}
          </div>
        )}

        {mode === 'view' && profiles.length > 1 && onViewOtherProfiles && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onViewOtherProfiles}
              className="px-3 py-2 border border-primary-700 rounded-md text-sm font-medium transition-colors bg-primary-500/80 text-white hover:bg-primary-700"
            >
              {t('inventory.finishedGoodsTaste.viewOtherProfiles')}
            </button>
          </div>
        )}

        <div className="space-y-4">
          <TasteSliderRow
            control={control}
            name="acidity"
            label={t('cupping.formSections.labels.acidity')}
            leftLabel="Low"
            centerLabel="Medium"
            rightLabel="High"
            disabled={readOnly}
          />
          <TasteSliderRow
            control={control}
            name="body"
            label={t('recipes.tasting.attributes.Body')}
            leftLabel="Light"
            centerLabel="Medium"
            rightLabel="Full"
            disabled={readOnly}
          />
          <TasteSliderRow
            control={control}
            name="sweetness"
            label={t('cupping.formSections.labels.sweetness')}
            leftLabel="Low"
            centerLabel="Medium"
            rightLabel="High"
            disabled={readOnly}
          />
        </div>

        <div className="space-y-2">
          <span className="block text-sm font-medium text-gray-700">{t('qcCupping.flavorNotes')}</span>
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {FLAVOR_HIERARCHY.map(group => {
                const parentChecked = flavorNotes.includes(group.name);
                return (
                  <div key={group.name} className="space-y-1">
                    <label
                      className={clsx(
                        'inline-flex items-center gap-2 font-semibold text-gray-800',
                        readOnly ? 'cursor-not-allowed' : 'cursor-pointer'
                      )}
                    >
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded-sm border-gray-400 text-gray-900 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        checked={parentChecked}
                        onChange={() => handleFlavorToggle(group.name)}
                        data-test-id={`finished-goods-taste-flavor-parent-${group.name}`}
                        disabled={readOnly}
                      />
                      <span>{group.name}</span>
                    </label>
                    {group.children && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {group.children.map(child => {
                          const checked = flavorNotes.includes(child);
                          return (
                            <label
                              key={child}
                              className={clsx(
                                'inline-flex items-center gap-1 whitespace-nowrap',
                                (readOnly || !parentChecked) ? 'cursor-not-allowed' : 'cursor-pointer',
                                !parentChecked && 'opacity-40'
                              )}
                            >
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 rounded-sm border-gray-300 text-gray-700 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                checked={checked}
                                disabled={!parentChecked || readOnly}
                                onChange={() => handleFlavorToggle(child)}
                                data-test-id={`finished-goods-taste-flavor-child-${child}`}
                              />
                              <span className="text-gray-600">{child}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {flavorNotes.length === 0 && (
            <p className="mt-1 text-xs text-red-600" data-test-id="finished-goods-taste-flavor-error">
              {t('inventory.finishedGoodsTaste.errors.selectFlavorNote')}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="notes">
            {t('reports.details.notes')}
          </label>
          <textarea
            id="notes"
            rows={3}
            {...register('notes')}
            data-test-id="finished-goods-taste-notes"
            className="mt-1 block w-full rounded-md p-2 border border-gray-400 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            placeholder={readOnly ? '' : t('inventory.finishedGoodsTaste.notesPlaceholder')}
            disabled={readOnly}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            data-test-id="finished-goods-taste-cancel"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            {readOnly ? t('common.actions.close') : t('auth.common.cancel')}
          </button>
          {!readOnly && (
            <button
              type="submit"
              disabled={
                submitting ||
                !!errors.acidity ||
                !!errors.body ||
                !!errors.sweetness ||
                flavorNotes.length === 0
              }
              data-test-id="finished-goods-taste-save"
              className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t('forms.actions.saving') : t('inventory.finishedGoodsTaste.saveTasteProfile')}
            </button>
          )}
        </div>
      </form>
    </>
  );
};
